import React, { useEffect, useState } from 'react'
import axios from 'axios'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router'
import './index.css'
import routes from './routing'
import { PublicClientApplication } from '@azure/msal-browser'
import { loginRequest, msalConfig } from './authConfig'
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react'
import { datadogRum } from '@datadog/browser-rum'
import { reactPlugin } from '@datadog/browser-rum-react'
import { createTheme, ThemeProvider } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { DataProvider } from './hooks'

axios.defaults.baseURL = process.env.REACT_APP_BACKEND_ENDPOINT ?? ''
axios.defaults.timeout = 5 * 60 * 1000
dayjs.extend(utc)

const msalInstance = new PublicClientApplication(msalConfig)

const router = createBrowserRouter(routes)

axios.interceptors.request.use(
  async config => {
    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/access`],
        account: msalInstance.getActiveAccount() ?? undefined,
        forceRefresh: true,
      })

      config.headers.Authorization = response.accessToken
      return config
    } catch (error) {
      const response = await msalInstance.acquireTokenPopup({
        scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/access`],
      })

      config.headers.Authorization = response.accessToken
      return config
    }
  },
  error => {
    return Promise.reject(error)
  },
)

datadogRum.init({
  applicationId: '501143dd-34dd-4183-bab0-64fe132e2cf2',
  clientToken: 'pub472e95a2100ef404054c8e09fb31aab0',
  site: 'us5.datadoghq.com',
  service: 'order-portal',
  env: process.env.REACT_APP_DATADOG_ENV ?? 'dev',
  version: '1.0.0',
  sessionSampleRate: 100,
  sessionReplaySampleRate: process.env.REACT_APP_DATADOG_ENV === 'prod' ? 100 : 0,
  defaultPrivacyLevel: 'allow',
  plugins: [reactPlugin({ router: true })],
  startSessionReplayRecordingManually: true,
  trackViewsManually: true,
})

const palette = createTheme({
  palette: {
    primary: {
      main: '#32578b',
      contrastText: '#FFFFFF',
    },
  },
})

const theme = createTheme({
  cssVariables: true,
  typography: {
    fontFamily: 'Arial, sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        outlined: {
          backgroundColor: 'white',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          tableLayout: 'fixed',
          variants: [
            {
              props: { color: 'primary' },
              style: {
                '& .MuiTableHead-root .MuiTableCell-root': {
                  backgroundColor: palette.palette.primary.main,
                  color: palette.palette.primary.contrastText,
                },
                '& .MuiTableHead-root .MuiTableSortLabel-root, & .MuiTableHead-root .MuiTableSortLabel-root svg, & .MuiTableHead-root .MuiIconButton-root':
                  {
                    color: palette.palette.primary.contrastText,
                  },
                '& .MuiTableHead-root .MuiCheckbox-root': {
                  color: palette.palette.primary.contrastText,
                },
                '& .MuiTableBody-root': {
                  background: 'white',
                },
              },
            },
          ],
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          '& > p': {
            margin: 0,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        list: {
          height: '100%',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            background: 'white',
          },
        },
      },
    },
  },
  palette: palette.palette,
})

function App() {
  const isAuthenticated = useIsAuthenticated()
  const { instance, inProgress } = useMsal()

  const [email, setEmail] = useState('')

  useEffect(() => {
    if (inProgress !== 'none') {
      return
    }

    if (isAuthenticated) {
      instance.setActiveAccount(instance.getAllAccounts()[0])
      const msalEmail = instance.getActiveAccount()?.username ?? ''

      if (!email) {
        setEmail(msalEmail)
      }

      datadogRum.setUser({
        id: msalEmail,
        email: msalEmail,
      })
      datadogRum.startView()
      datadogRum.startSessionReplayRecording()
    } else {
      instance.loginRedirect(loginRequest).catch(e => {
        console.log(e)
      })

      instance.handleRedirectPromise().then(() => {
        if (instance.getAllAccounts().length > 0) {
          instance.setActiveAccount(instance.getAllAccounts()[0])

          const msalEmail = instance.getActiveAccount()?.username ?? ''
          setEmail(msalEmail)
        }
      })
    }
  }, [inProgress, isAuthenticated, instance])

  return isAuthenticated ? (
    <DataProvider email={email}>
      <RouterProvider router={router} />
    </DataProvider>
  ) : (
    <div>Signing in...</div>
  )
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
root.render(
  <React.StrictMode>
    <MsalProvider instance={msalInstance}>
      <ThemeProvider theme={theme}>
        <App />
      </ThemeProvider>
    </MsalProvider>
  </React.StrictMode>,
)
